import { useCallback, useEffect, useState } from 'react'

import { useServices } from '../../services/ServicesProvider.js'

/**
 * Overlay combinations worth watching, kept between sessions (T-404).
 *
 * A combination is a set of **tracks only** — never a set of tracks and a date
 * range. Recalling one applies it to whatever range is currently selected,
 * which is the whole use: the pairing is the thing you keep coming back to, and
 * the window changes every time you look.
 *
 * Stored in the `Storage` port's settings table rather than in `localStorage`,
 * so it travels in the JSON backup (T-502) and the native app inherits it for
 * free. `localStorage` would be one more thing silently lost on a restore.
 */

const KEY = 'insights.overlays'

export interface SavedOverlay {
  readonly id: string
  readonly trackIds: readonly string[]
}

function parse(raw: string | undefined): SavedOverlay[] {
  if (raw === undefined) {
    return []
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    // `Array.isArray` narrows to `any[]`, which would let anything through the
    // checks below untyped. Whatever came out of storage is `unknown` until it
    // has been looked at.
    return (parsed as unknown[]).filter(
      (entry): entry is SavedOverlay =>
        typeof entry === 'object' &&
        entry !== null &&
        'id' in entry &&
        typeof entry.id === 'string' &&
        'trackIds' in entry &&
        Array.isArray(entry.trackIds),
    )
  } catch {
    return []
  }
}

export function useSavedOverlays() {
  const { storage, ids } = useServices()
  const [saved, setSaved] = useState<SavedOverlay[]>([])

  const load = useCallback(async () => {
    setSaved(parse(await storage.settings.get(KEY)))
  }, [storage])

  useEffect(() => {
    void load()
  }, [load])

  const write = useCallback(
    async (next: SavedOverlay[]) => {
      await storage.settings.set(KEY, JSON.stringify(next))
      setSaved(next)
    },
    [storage],
  )

  const save = useCallback(
    async (trackIds: readonly string[]) => {
      const key = [...trackIds].sort().join(',')
      const existing = parse(await storage.settings.get(KEY))
      // Saving the same pairing twice should be a no-op, not a second row that
      // looks identical and cannot be told apart.
      if (existing.some((entry) => [...entry.trackIds].sort().join(',') === key)) {
        setSaved(existing)
        return
      }
      await write([...existing, { id: ids.next(), trackIds: [...trackIds] }])
    },
    [storage, ids, write],
  )

  const remove = useCallback(
    async (id: string) => {
      const existing = parse(await storage.settings.get(KEY))
      await write(existing.filter((entry) => entry.id !== id))
    },
    [storage, write],
  )

  return { saved, save, remove }
}
